import { calculatingStep, schoolProps } from "./schoolProperties";

// It is DECORATIVE ONLY — it never gates, delays or cancels the run, and the X closes the window
// while the job carries on. That is explicit rule and it is the load-bearing property here: an
// animation that could affect a dispatched job would be a worse thing than no animation.

// The frames now arrive pre-built with their labels IN ORDER, so a second copy of that vocabulary
// here would be a second thing to drift from list — the same trap as re-deriving any other
// agent's column names.

// Genius Map only. Buffer pH is not a step: that school has no model for it.
export const SUBTITLE_STEPS = schoolProps("ml").map(calculatingStep);

export const TIMING = {
  covariatesPerSecond: 5,
  /* * how long the MAP plays before it fades */
  mapMs: 3000,
  /* * the fade itself — "dissolving for roughly a second" */
  fadeMs: 1000,
  /* * the subtitle's own cadence; independent of the map */
  subtitleMs: 900,
  /*
   *
   * Longer than the map variant's 3 s because this one has somewhere to go afterwards — the points
   * then the order confirmation.
   */
  sampleMs: 5000,
} as const;

export const covariateMs = 1000 / TIMING.covariatesPerSecond;

export interface AnimCell {
  /* * normalised 0..1 within the field's bbox, y DOWN (screen space) */
  x: number;
  y: number;
  /*
   *
   * Behaviour-Classified Zones has to bin a percentile into its decile, and Classic Grid has to
   * read which exclusion rules fire — both are questions about the NUMBER, and a decoder that keeps
   * only the paint answers neither.
   */
  values: (number | null)[];
  /* * one colour per frame, index-aligned with the served `labels` */
  colors: string[];
}

/* * How many times the spiral arm sweeps round on its way out. */
export const SPIRAL_TURNS = 2;

/*
 *
 * BOUNDED TO [0,1] BY CONSTRUCTION, and that is load-bearing rather than tidy: the farthest cell
 * must land exactly at the end of its covariate's window, or the next covariate begins while some
 * tiles still show the last one.
 */
export function flipDelayFraction(
  cell: { x: number; y: number },
  centre = { x: 0.5, y: 0.5 },
): number {
  const dx = cell.x - centre.x;
  const dy = cell.y - centre.y;
  const d = Math.sqrt(dx * dx + dy * dy);
  // the farthest a point in the unit square can be from its centre
  const max = Math.SQRT1_2;
  const radius = Math.min(1, d / max);
  // 0..1 around the circle; `atan2(0,0)` is 0, so the exact centre is well-defined rather than NaN
  const angle = (Math.atan2(dy, dx) + Math.PI) / (2 * Math.PI);
  return (radius * SPIRAL_TURNS + angle) / (SPIRAL_TURNS + 1);
}

/*
 *
 * `count` is how many frames actually resolved — a field carrying 8 of the 20 loops through 8
 * rather than running off the end into blanks.
 */
export function covariateIndexAt(tMs: number, count = 0): number {
  const i = Math.max(0, Math.floor(tMs / covariateMs));
  return count > 0 ? i % count : i;
}

/*
 *
 * Takes plain labels rather than a covariate catalog: the frames arrive pre-built with their names
 * in order, so the FE holds no copy of the vocabulary to disagree with compute's.
 */
export function captionFor(
  index: number,
  labels: readonly string[],
): string | null {
  const label = labels[index % Math.max(1, labels.length)];
  return label ? `Checking ${label}` : null;
}

// High-Res has two workflows: "use existing samples" runs the model, and the other DESIGNS a
// sampling plan and orders it. They are different work and deserve different words, but the same
// picture: the field's covariates flipping past says "we are reading your ground" either way.

export const SAMPLE_SUBTITLE_STEPS = [
  "Collecting Field Data…",
  "Avoiding Waterways & Inside Corners…",
  "Running K-Means Clustering…",
  "Trying Different Combinations…",
  "Representing Real Field Variability…",
] as const;

export const BEHAVIOR_SUBTITLE_STEPS = [
  "Fetching Field Data…",
  "Running Rainfall & Diffusion Simulator…",
  "Checking Yield/NDVI Layers…",
  "Looking Up County Avg Yield…",
  "Analyzing AI Fertility Maps…",
] as const;

export const RELATIVE_SUBTITLE_STEPS = [
  "Collecting Field Data…",
  "Checking Soil Types…",
  "Analyzing Topographic Water Flow…",
  "Looking at Yield/NDVI…",
  "Deciding the Ideal Zone Number…",
] as const;

export const GRID_SUBTITLE_STEPS = [
  "Generating Grid…",
  "Avoiding Waterways…",
  "Avoiding Inside Corners…",
  "Creating Points…",
] as const;

export type AnimVariant = "map" | "sample" | "behavior" | "relative" | "grid";

/*
 *
 * `kind` IS WHAT ACT TWO DRAWS, and it is here rather than at each call site because it is a
 * property of the SCHOOL, not of the click: a design is points or it is zones, and the heading, the
 * geometry renderer and the confirmation all have to agree about which.
 */
export const VARIANTS: Record<
  AnimVariant,
  {
    title: (n: number) => string;
    subtitles: readonly string[];
    scanner: boolean;
    kind: "points" | "zones";
  }
> = {
  map: {
    title: (n) =>
      `Using AI to Render High Resolution Maps for ${n} Field${n === 1 ? "" : "s"}`,
    subtitles: SUBTITLE_STEPS,
    scanner: false,
    // the map variant has no act two at all — it loops with the background note — but the field is
    // required, and points is the honest answer for the High-Res workflow it belongs to
    kind: "points",
  },
  /*
   *
   * wrote each of these three without one, exactly as he did for the sample variant, so they ignore
   * their argument.
   */
  behavior: {
    title: () => "Sorting Fields By Wetness, Productivity, and Fertility",
    subtitles: BEHAVIOR_SUBTITLE_STEPS,
    scanner: false,
    kind: "zones",
  },
  relative: {
    title: () => "Splitting the Field into Uniform Zones",
    subtitles: RELATIVE_SUBTITLE_STEPS,
    scanner: false,
    kind: "zones",
  },
  grid: {
    title: () => "Laying Out Sample Grid",
    subtitles: GRID_SUBTITLE_STEPS,
    scanner: false,
    kind: "points",
  },
  sample: {
    // No field count here — gave this title without one, and inventing a "for N fields" tail to
    // match its sibling would be a change he did not ask for.
    title: () => "Using AI to Pick Sample Points",
    subtitles: SAMPLE_SUBTITLE_STEPS,
    scanner: true,
    kind: "points",
  },
};

/* * Cells per second each head advances. Fast enough to read as motion, slow enough to follow. */
export const SCAN_CELLS_PER_SEC = 90;
/*
 *
 * spacing: "New white squares should appear every 20 pixels.
 */
export const SCAN_SPACING = 20;

/*
 *
 * THE INTERIM CELLS ARE NOT A GRID. They are subsampled centroids, so "the next square in the row"
 * has to be derived rather than read off an index — the served frame set IS a raster and this
 * becomes the identity, but until then the scan has to bin by row or it would jump around the field
 * instead of sweeping it.
 */
export function scanOrder(
  cells: { x: number; y: number }[],
  aspect = 1,
): number[] {
  const n = cells.length;
  if (!n) return [];
  const rows = Math.max(1, Math.round(Math.sqrt(n / Math.max(0.05, aspect))));
  const keyed = cells.map((c, i) => ({
    i,
    r: Math.min(rows - 1, Math.floor(c.y * rows)),
    x: c.x,
  }));
  keyed.sort((a, b) => a.r - b.r || a.x - b.x);
  const order = new Array<number>(n);
  keyed.forEach((k, pos) => {
    order[k.i] = pos;
  });
  return order;
}

/*
 *
 * Is the cell at `order` currently white?
 *
 * `head` is how many cells the leader has advanced. A follower exists at every `SCAN_SPACING` behind
 * it — but only once the leader has travelled that far, which is what makes the train BUILD rather
 * than appear all at once on the first frame.
 */
export function isScanned(
  order: number,
  n: number,
  head: number,
  spacing = SCAN_SPACING,
): boolean {
  if (n <= 0) return false;
  const back = (((head - order) % n) + n) % n;
  return back % spacing === 0 && back <= head;
}
